<template>
  <VDialog
    v-model="modelShow"
    max-width="640px"
    :z-index="9999"
    attach="body"
    data-testid="dialog-patients-with-errors"
  >
    <VCard class="flex flex-col gap-4 p-6 sm:p-8 rounded-lg bg-white">
      <div class="flex justify-between items-start">
        <div class="flex flex-col gap-1 pr-4">
          <h3 class="text-h6 font-bold text-gray-900" data-testid="modal-title">
            {{
              t('AKI risk unavailable ({patients})', {
                patients: t(
                  '{count} patients | {count} patient | {count} patients',
                  { count: patientErrors.length },
                  patientErrors.length
                )
              })
            }}
          </h3>
          <p class="text-sm text-gray-600" data-testid="modal-description">
            {{
              t(
                'Acute kidney injury (AKI) risk could not be scored for {patients} because AKI service is unavailable on their ward or required parameters do not fit the requirements (e.g. missing, out of range, expired).',
                {
                  patients: t(
                    '{count} patients | {count} patient | {count} patients',
                    { count: patientErrors.length },
                    patientErrors.length
                  )
                }
              )
            }}
          </p>
        </div>
        <VBtn
          icon="close"
          variant="plain"
          size="small"
          class="text-gray-500 -mt-1 -mr-2"
          :ripple="false"
          data-testid="btn-close"
          @click="closeDialog"
        />
      </div>

      <div
        class="flex flex-col gap-4 mt-2 max-h-[400px] overflow-y-auto"
        data-testid="patients-list"
      >
        <div
          v-for="(error, index) in patientErrors"
          :key="error.caseId ?? index"
          class="flex items-center gap-3"
          data-testid="patient-error-item"
        >
          <template v-if="error.case?.patient">
            <PersonAvatar
              :first-name="error.case.patient.firstName ?? ''"
              :last-name="error.case.patient.lastName ?? ''"
              type="patient"
            />
            <div
              class="cursor-pointer text-[#1D5D77] font-titillium text-base tracking-[0.15px] leading-[1.5] font-semibold"
              data-testid="patient-name"
              @click="openPatientModal(error.caseId)"
            >
              {{ getPatientDisplayName(error.case.patient) }}
            </div>
          </template>
          <template v-else>
            <div
              class="text-gray-500 font-titillium text-base tracking-[0.15px] leading-[1.5]"
              data-testid="patient-not-found"
            >
              {{ t('Patient {caseId} details not found', { caseId: error.caseId }) }}
            </div>
          </template>
        </div>
      </div>

      <VProgressLinear v-if="caseLoading" indeterminate color="primary" class="mt-2" />
    </VCard>
  </VDialog>

  <VitalSignsPreviewModal
    v-if="caseWithVitalSigns"
    v-model:show="showVitalSignsPreviewModal"
    :caze="caseWithVitalSigns.case"
    :vital-signs="caseWithVitalSigns.vitalSigns"
    :laboratory="caseWithVitalSigns.laboratory"
    :organ-support="caseWithVitalSigns.organSupport"
    :share-code="shareCode"
    :eds-score="caseWithVitalSigns.edsScore"
  />
</template>

<script setup lang="ts">
import type { AkiOverviewErrorDTO, PatientDTO } from '@/generated'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { VBtn, VCard, VDialog, VProgressLinear } from 'vuetify/components'
import PersonAvatar from '@/components/navigation/PersonAvatar.vue'
import VitalSignsPreviewModal from '@/components/patient/components/molecules/VitalSignsPreviewModal.vue'
import { useConsultationPatient } from '@/composables/useConsultationPatient'
import { usePatientsWithVitalSignsStore } from '@/stores/patientsWithVitalSigns'

interface Props {
  patientErrors: AkiOverviewErrorDTO[]
}

defineProps<Props>()

const modelShow = defineModel<boolean>('show', { default: false })

const { t } = useI18n()
const route = useRoute()
const { formatPatientDTODetails } = useConsultationPatient()

const patientsWithVitalSignsStore = usePatientsWithVitalSignsStore()
const { caseWithVitalSigns } = storeToRefs(patientsWithVitalSignsStore)

const showVitalSignsPreviewModal = ref(false)
const caseLoading = ref<boolean>(false)

const shareCode = computed(() => {
  return route?.query?.shareCode as string | undefined
})

function closeDialog() {
  modelShow.value = false
}

function getPatientDisplayName(patient: PatientDTO): string {
  const details = formatPatientDTODetails(patient)
  const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ')
  return details ? `${name} (${details})` : name
}

async function openPatientModal(patientCaseId?: string, patientWardId?: string) {
  if (!patientCaseId || caseLoading.value) return

  showVitalSignsPreviewModal.value = false
  caseLoading.value = true

  await patientsWithVitalSignsStore.getCaseWithVitalSigns(
    patientCaseId,
    patientWardId ?? '',
    shareCode.value
  )

  if (caseWithVitalSigns.value) {
    showVitalSignsPreviewModal.value = true
  }

  caseLoading.value = false
}
</script>
